import React, { useEffect, useRef, useState } from 'react';
import { fromLonLat } from 'ol/proj';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import { defaults as defaultControls, Zoom } from 'ol/control';
import 'ol/ol.css';

import bus from '../../../assets/marker_bus.png';
import { Box, ToggleButton,Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MyLocationIcon from '@mui/icons-material/MyLocation';

function Openlayer({ position }) {
  const mapEl = useRef(null);
  const theme = useTheme();
  const view = useRef(new View({ zoom: 15, maxZoom: 20 })).current;
  const vectorSource = useRef(new VectorSource()).current;
  const hasFocused = useRef(false);
  const [follow, setFollow] = useState(false);

  // Map initialization (once)
  useEffect(() => {
    if (!mapEl.current) return;

    const vectorLayer = new VectorLayer({ source: vectorSource });

    const map = new Map({
      target: mapEl.current,
      layers: [new TileLayer({ source: new OSM() }), vectorLayer],
      view,
      controls: defaultControls({ attribution: false, zoom: false }).extend([
        new Zoom({ className: 'mui-zoom-control' })
      ])
    });

    // Style zoom buttons with MUI theme
    setTimeout(() => {
      const el = document.querySelector('.mui-zoom-control');
      if (el) {
        Object.assign(el.style, {
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '12px',
          boxShadow: '0 3px 6px rgba(0,0,0,0.25)',
          background: theme.palette.background.paper,
          overflow: 'hidden',
          zIndex: 1000
        });
        el.querySelectorAll('button').forEach(btn => {
          Object.assign(btn.style, {
            width: '40px',
            height: '40px',
            fontSize: '20px',
            border: 'none',
            cursor: 'pointer'
          });
          btn.addEventListener('mouseenter', () => {
            btn.style.background = theme.palette.primary.main;
            btn.style.color = theme.palette.primary.contrastText;
          });
          btn.addEventListener('mouseleave', () => {
            btn.style.background = 'transparent';
            btn.style.color = theme.palette.text.primary;
          });
        });
      }
    }, 300);

    return () => map.setTarget(undefined);
  }, [theme]);

  // First time focus on marker
  useEffect(() => {
    if (!position?.latitude || !position?.longitude) return;
    if (hasFocused.current) return;

    const coord = fromLonLat([position.longitude, position.latitude]);
    view.animate({ center: coord, zoom: 16, duration: 800 });
    hasFocused.current = true;
  }, [position]);

  // Update marker + follow mode
  useEffect(() => {
    if (!position?.latitude || !position?.longitude) return;

    const coord = fromLonLat([position.longitude, position.latitude]);

    // Update marker
    vectorSource.clear();
    const feature = new Feature({ geometry: new Point(coord) });
    feature.setStyle(
      new Style({
        image: new Icon({
          anchor: [0.5, 1],
          src: bus,
          scale: 0.45
        })
      })
    );
    vectorSource.addFeature(feature);

    // Follow only if enabled
    if (follow) {
      view.animate({ center: coord, zoom: 16, duration: 800 });
    }
  }, [position, follow]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100vh' }}>
      {/* Map */}
      <Box
        ref={mapEl}
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: 1
        }}
      />

      {/* Follow Button - Top Left */}
      <Tooltip title={follow ? 'Following' : 'Follow vehicle'} placement="left">
      <ToggleButton
        value="follow"
        selected={follow}
        onChange={() => setFollow(!follow)}
        aria-label="Follow vehicle"
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1000,
          bgcolor: follow ? theme.palette.primary.main : theme.palette.background.paper,
          color: follow ? theme.palette.primary.contrastText : theme.palette.text.primary,
          '&:hover': {
            bgcolor: follow ? theme.palette.primary.dark : theme.palette.action.hover
          }
        }}
      >
        <MyLocationIcon />
      </ToggleButton>
      </Tooltip>
    </Box>
  );
}

export default Openlayer;